<template>
  <b-card>
    <b-alert v-model="showAlert" variant="danger" dismissible>
      <span v-html="alertMessage"></span>
    </b-alert>
    <b-form @submit.prevent>
      <div class="row">
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="productId"
            label="SKU"
            label-for="productId"
            label-class="form-label required"
          >
            <b-form-input
              id="productId"
              type="text"
              v-model="form.productId"
              :disabled="mode == 'edit'"
            ></b-form-input>
          </b-form-group>
        </div>
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="finanPlanType"
            label="Plan Type"
            label-for="finanPlanType"
            label-class="form-label required"
          >
            <b-form-input
              id="finanPlanType"
              type="text"
              v-model="form.finanPlanType"
              :disabled="mode == 'edit'"
            ></b-form-input>
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="finanPaymentType"
            label="Payment Type"
            label-for="finanPaymentType"
            label-class="form-label required"
          >
            <b-form-input
              id="finanPaymentType"
              type="text"
              v-model="form.finanPaymentType"
              :disabled="mode == 'edit'"
            ></b-form-input>
          </b-form-group>
        </div>
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="finanMonths"
            label="Months"
            label-for="finanMonths"
            label-class="form-label required"
          >
            <b-form-input
              id="finanMonths"
              type="text"
              v-model="form.finanMonths"
              :disabled="mode == 'edit'"
            ></b-form-input>
          </b-form-group>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="finanPlanHash"
            label="Plan Hash"
            label-for="finanPlanHash"
            label-class="form-label required"
          >
            <b-form-input
              id="finanPlanHash"
              v-model="form.finanPlanHash"
              placeholder="Enter plan hash"
              :class="{ 'is-invalid': $v.form.finanPlanHash.$error }"
              @keypress="allowOnlyAlphaNumeric"
            ></b-form-input>
            <b-form-invalid-feedback id="finanPlanHash-feedback">
              {{ sytemMessages.finanPlanHashRequired }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="finanPlanDefault"
            label="Is Default"
            label-for="finanPlanDefault"
            label-class="form-label required "
            v-if="loaded && form.finanPaymentType != 'Deferred Payment'"
          >
          <div  class="d-flex gap-3">
            <b-form-radio
                v-model="form.finanPlanDefault"
                name="radio-options-finanPlanDefault"
                :value="true"
              >
              Yes 
              </b-form-radio> &nbsp;
              <b-form-radio
                v-model="form.finanPlanDefault"
                name="radio-options-finanPlanDefault"
                :value="false"
              >
              No 
              </b-form-radio>
          </div>
            <b-form-invalid-feedback id="finanPlanDefault-feedback">
              {{ sytemMessages.finanPlanDefaultRequired }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="finanStartDate"
            label="Start Date"
            label-for="finanStartDate"
            label-class="form-label required"
          >
            <b-form-input
              id="finanStartDate"
              type="datetime-local"
              v-model="form.finanStartDate"
              step="1"
              placeholder="Select Start Date"
              :max="form.finanEndDate"
              :state="getFieldState($v.form.finanStartDate)"
              @change="$v.form.finanStartDate.$touch()"
              :class="{
                'is-invalid': $v.form.finanStartDate.$error,
              }"
            ></b-form-input>
            <b-form-invalid-feedback v-if="$v.form.finanStartDate.$error">
              <template v-if="!$v.form.finanStartDate.required">
                {{ sytemMessages.finanStartDateRequired }}
              </template>
              <template v-else-if="!$v.form.finanStartDate.isStartBeforeEnd">
                {{ sytemMessages.finanStartDateMustSmallerEndDate }}
              </template>
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="finanEndDate"
            label="End Date"
            label-for="finanEndDate"
            label-class="form-label required"
          >
            <b-form-input
              id="finanEndDate"
              type="datetime-local"
              step="1"
              v-model="form.finanEndDate"
              placeholder="Select End Date"
              :min="form.finanStartDate"
              :state="getFieldState($v.form.finanEndDate)"
              @change="$v.form.finanEndDate.$touch()"
              :class="{
                'is-invalid': $v.form.finanEndDate.$error,
              }"
            ></b-form-input>
            <b-form-invalid-feedback v-if="$v.form.finanEndDate.$error">
              <template v-if="!$v.form.finanEndDate.required">
                {{ sytemMessages.finanEndDateRequired }}
              </template>
              <template v-else-if="!$v.form.finanEndDate.isEndAfterStart">
                {{ sytemMessages.finanEndDateMustGreaterStartDate }}
              </template>
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="finanPaymentFactor"
            label="Payment Factor"
            label-for="finanPaymentFactor"
            label-class="form-label required"
          >
            <b-form-input
              id="finanPaymentFactor"
              type="text"
              v-model="form.finanPaymentFactor"
              placeholder="Enter Payment Factor"
              :class="{
                'is-invalid': $v.form.finanPaymentFactor.$error,
              }"
              @keypress="allowPositiveDecimal($event, 10)"
            ></b-form-input>
            <b-form-invalid-feedback id="finanPaymentFactor-feedback">
              {{ sytemMessages.finanPaymentFactorRequired }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="finanMonthlyApr"
            label="Monthly Apr"
            label-for="finanMonthlyApr"
            label-class="form-label required"
          >
            <b-form-input
              id="finanMonthlyApr"
              type="text"
              v-model="form.finanMonthlyApr"
              placeholder="Enter Monthly Apr"
              :class="{
                'is-invalid': $v.form.finanMonthlyApr.$error,
              }"
              @keypress="allowPositiveDecimal($event)"
            ></b-form-input>
            <b-form-invalid-feedback id="finanMonthlyApr-feedback">
              {{ sytemMessages.finanMonthlyAprRequired }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </div>


      <div class="row">
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="finanMinPurchase"
            label="Min Purchase Amount"
            label-for="finanMinPurchase"
            label-class="form-label"
          >
            <b-form-input
              id="finanMinPurchase"
              type="text"
              v-model="form.finanMinPurchase"
              placeholder="Enter Min Purchase Amount"
              :class="{
                'is-invalid': $v.form.finanMinPurchase.$error,
              }"
              @keypress="allowPositiveDecimal($event)"
            ></b-form-input>
            <b-form-invalid-feedback id="finanMinPurchase-feedback">
              {{ sytemMessages.finanMinPurchaseRequired }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
        <div class="col-sm-12 col-md-6">
          <b-form-group
            id="isEnabled"
            label="Is Enabled"
            label-for="isEnabled"
            label-class="form-label "
           
          >
            <div  class="d-flex gap-3">
              <b-form-radio
                  v-model="form.isEnabled"
                  name="radio-options-isEnabled"
                  :value="true"
                >
                Enabled 
                </b-form-radio> &nbsp;
                <b-form-radio
                  v-model="form.isEnabled"
                  name="radio-options-isEnabled"
                  :value="false"
                >
                Disabled
                </b-form-radio>
            </div>
            <b-form-invalid-feedback id="isEnabled-feedback">
              {{ sytemMessages.isEnabledRequired }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12 col-md-6">
          <b-form @submit.prevent>
            <b-form-group
              id="aslowascategories"
              label="As Low As Categories"
              label-for="aslowascategories"
              label-class="form-label "
            >
              <Multiselect
                v-model="form.asLowAsCategories"
                :options="formattedCategoryNames"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Select categories"
                label="text"
                track-by="value"
                :disabled="isFieldDisabled"
              />
            </b-form-group>
          </b-form>

        </div>
        <div class="col-sm-12 col-md-6">
          <b-form @submit.prevent>
            <b-form-group
              id="excludeTags"
              label="Exclude Tags"
              label-for="excludeTags"
              label-class="form-label "
            >
              <Multiselect
                v-model="form.excludeTags"
                :options="formattedCategoryNames"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Select categories"
                label="text"
                track-by="value"
              />
            </b-form-group>
          </b-form>

        </div>
      </div>

      <div class="d-flex flex-row-reverse">
        <b-button @click="handleSave" variant="brand-primary">Save</b-button>
        <b-button @click="$router.push({ name: 'fairstone-plans-list' })" variant="brand-info">Cancel</b-button>
      </div>
    </b-form>
  </b-card>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import { required } from "vuelidate/lib/validators";
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'

export default {
  name: "Form",
  props: ["mode"],
  components: { Multiselect },
  data() {
    return {
      showAlert: false,
      loaded: false,
      alertMessage: "",
      excludeTagsSearchText: "",
      asLowAsCategoriesSearchText: "",
      filterOn: [],
      asLowAsMultiSelect: false,
      form: {
        siteId: 0,
        productId: 0,
        isEnabled: 0,
        finanPlanType: "",
        finanPaymentType: "",
        excludeProvinces: [],
        finanMonths: "",

        finanPlanHash: "",
        finanStartDate: null,
        finanEndDate: null,
        finanPaymentFactor: 0,
        finanMonthlyApr: 0,
        finanMinPurchase: 0,
        finanPlanDefault: false,
        excludeTags: [],
        asLowAsCategories: [],
      },
    };
  },
  async mounted() {
    await this.getCategoryOptions();
    if (this.mode == "edit") {
      if (
        (this.$route.params.siteId && this.$route.params.siteId != 0 && this.$route.params.siteId != "") &&
        (this.$route.params.productId && this.$route.params.productId != 0 && this.$route.params.productId != "")
      ) {
        let siteId = this.$route.params.siteId;
        let productId = this.$route.params.productId;

        this.generalSetItem({ resource: "isLoading", payload: true });

        this.getById({ siteId: siteId, productId: productId, userId: this.loggedUserId })
          .then((data) => {
            this.generalSetItem({ resource: "isLoading", payload: false });
            this.loaded = true;
            if (data && typeof data === 'object') {
              data.asLowAsCategories = Array.isArray(data.asLowAsCategories) ? data.asLowAsCategories.map(val =>
                this.formattedCategoryNames.find(option => option.value === val)
              ).filter(Boolean) : [];
              data.excludeTags = Array.isArray(data.excludeTags) ? data.excludeTags.map(val =>
                this.formattedCategoryNames.find(option => option.value === val)
              ).filter(Boolean) : [];
              this.form = Object.assign({}, data);
            } else {
              console.warn("Unexpected response format:", response);
            }
          })
          .catch((error) => {
            this.generalSetItem({ resource: "isLoading", payload: false });
            this.loaded = true;
            //return back to listing page because error in fetch record
            this.$router.push({ name: "fairstone-plans-list" });
          });
      } else {
        this.$router.push({ name: "fairstone-plans-list" });
      }
    }
  },
  computed: {
    isFieldDisabled() {
      return this.asLowAsMultiSelect || (this.loaded && this.form.finanPaymentType == 'Deferred Payment');
    },
    ...mapGetters("fairstonePlansManager", ["categoryNames"]),
    formattedCategoryNames() {
      return this.categoryNames.map(category => ({
        value: category.id,
        text: category.value.replace(/,/g, ' > ')
      }));
    },
    selectedExcludeTags() {
      if (!Array.isArray(this.form.selectedExcludeTags)) return [];
      return this.form.excludeTags.map(tagId => {
        const category = this.categoryNames.find(cat => cat.id === tagId);
        return category ? category.value.replace(/,/g, ' > ') : '';
      });
    },
    selectedAsLowAsCategories() {
      if (!Array.isArray(this.form.asLowAsCategories)) return [];
      return this.form.asLowAsCategories.map(tagId => {
        const category = this.categoryNames.find(cat => cat.id === tagId);
        return category ? category.value.replace(/,/g, ' > ') : '';
      });
    }
  },
  watch: {
    'form.finanPlanDefault': 'handleDefaultPlanChange',
    'form.excludeTags': 'handleSameCategory',
    'form.asLowAsCategories'(newVal, oldVal) {
      this.handleDefaultPlanChange(newVal, oldVal);
      this.handleSameCategory(newVal, oldVal);
    }
  },
  methods: {
    ...mapMutations({
      generalSetItem: "setItem",
    }),
    ...mapActions("fairstonePlansManager", ["getById", "getCategoryOptions", "updatePlan"]),
    ...mapMutations("fairstonePlansManager", ["setItem"]),
    ...mapActions("siteManager", ["getStoreList"]),
    formatLocalDateTime(dateStr) {
      const date = new Date(dateStr);
      const pad = (n) => n.toString().padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },
    getFieldState(field) {
      const res = field.$dirty ? !field.$error : null;
      if (res == false) {
        return field.$dirty ? !field.$error : null;
      }
    },
    handleDefaultPlanChange() {
      this.asLowAsdefaultPlan();
    },
    handleSameCategory(){
      const excludeTags = this.form.excludeTags.map(item => item.value);
      const asLowAs = this.form.asLowAsCategories.map(item => item.value);

      const common = excludeTags.filter(tag => asLowAs.includes(tag));

      if (common.length > 0) {
        this.$bvToast.toast(
          `Category "${common[0]}" is selected in both Exclude Tags and As Low As Categories.`,
          {
            title: 'Category Conflict',
            variant: 'danger',
            autoHideDelay: 7000,
            solid: true,
          }
        );
        return true;
      }

      return false;
    },
    asLowAsdefaultPlan() {
      if (this.form.finanPlanDefault == false) {
        this.asLowAsMultiSelect = false;
        return;
      }
      if (this.form.finanPlanDefault && this.form.asLowAsCategories.length > 0) {
        const createElement = this.$createElement;
        const titleVNode = createElement("h5", {
          class: ["modal-title font-weight-bold"],
          domProps: {
            innerHTML: "Warning",
          },
        });

        const messageVNode = createElement("p", { class: ["text-center m-0 font-14"] }, [
          "As low as category will changed value on default"
        ]);

        this.$bvModal.msgBoxConfirm([messageVNode], {
          title: [titleVNode],
          size: "sm",
          dialogClass: "confirm-import-modal",
          buttonSize: "sm",
          okVariant: "brand-primary w-100",
          cancelVariant: "outline-secondary w-100",
          okTitle: "Yes",
          cancelTitle: "No",
          footerClass: "p-3 border-top-0 flex-nowrap gap-2",
          headerClass: "justify-content-center p-0 mt-3 border-bottom-0",
          hideHeaderClose: true,
          centered: true,
          noCloseOnBackdrop: true,
          noCloseOnEsc: true,
          hideFooter: false,
        })
        .then(value => {
          if (value) {
            this.deleteAsLowAsCategories('all');
            this.asLowAsMultiSelect = true;
          } else {
            this.form.finanPlanDefault = false;
          }
        });
        return;
      }
    },
    async handleSave() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      
      if (!this.form.isEnabled && this.form.finanPlanDefault) {
          this.$bvToast.toast("Only an active plan can be set as default.", {
            title: "Error",
            variant: "danger",
            autoHideDelay: 5000,
            solid: true,
          });
          return;
      }

      if (this.handleSameCategory()) {
        return;
      }

      this.generalSetItem({ resource: "isLoading", payload: true });

      const payload = {
        ...this.form,
        finanStartDate: this.formatLocalDateTime(this.form.finanStartDate),
        finanEndDate: this.formatLocalDateTime(this.form.finanEndDate),
        asLowAsCategories: this.form.asLowAsCategories.map(option => option.value),
        excludeTags: this.form.excludeTags.map(option => option.value)
      };

      this.updatePlan(payload)
        .then((response) => {
          this.generalSetItem({ resource: "isLoading", payload: false });

          if (response.status == 1) {
              this.$bvToast.toast("Plan updated.", {
                title: "Success",
                variant: "success",
                autoHideDelay: 5000,
                solid: true,
              });
          } else {
            this.showAlert = true;
            this.alertMessage = response.error && Array.isArray(response.error) ? response.error.map(err => `${err.Message}`).join('<br/>') : response?.error;
          }
        })
        .catch(() => {
          this.generalSetItem({ resource: "isLoading", payload: false });
        });
    },
    deleteExcludeTags(index) {
      this.form.excludeTags.splice(index, 1);
    },
    deleteAsLowAsCategories(target) {
      if (target === 'all') {
        this.form.asLowAsCategories = [];
      } else if (typeof target === 'number' && target >= 0) {
        this.form.asLowAsCategories.splice(target, 1);
      } else {
        console.warn('Invalid parameter passed to deleteAsLowAsCategories:', target);
      }
    }
  },
  validations: {
    form: {
      siteId: { required },
      productId: { required },
      isEnabled: { required },
      finanPlanType: { required },
      finanPaymentType: { required },
      excludeProvinces: { required },
      finanMonths: { required },
      finanPlanHash: { required },
      finanStartDate: { 
        required,
        isStartBeforeEnd: (value, vm) => {
          const end = vm?.finanEndDate;
          const start = value;
          if (!start || !end) return true;
          return new Date(start).getTime() < new Date(end).getTime();
        }
      },
      finanEndDate: { 
        required,
        isEndAfterStart: (value, vm) => {
          const start = vm?.finanStartDate;
          const end = value;
          if (!start || !end) return true;
          return new Date(end).getTime() > new Date(start).getTime();
        }
      },
      finanPaymentFactor: { required },
      finanMonthlyApr: { required },
      finanMinPurchase: {  },
      finanPlanDefault: { required },
      excludeTags: {  },
      asLowAsCategories: {  },
    },
  },
  destroyed() {
    this.form = {
      siteId: 0,
      productId: 0,
      isEnabled: 0,
      finanPlanType: "",
      finanPaymentType: "",
      excludeProvinces: [],
      finanMonths: "",

      finanPlanHash: "",
      finanStartDate: "",
      finanEndDate: "",
      finanPaymentFactor: 0,
      finanMonthlyApr: 0,
      finanMinPurchase: "",
      finanPlanDefault: false,
      excludeTags: [],
      asLowAsCategories: [],
    };
  },
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>